'use client';

import { useState } from 'react';
import { Eye, EyeOff, Lock, LockKeyhole, Mail, User } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiRoutes, getApiUrl } from '@/constants/api';
import { useAuth } from '@/contexts/auth-context';
import {
  BadRequestError,
  NetworkError,
  RateLimitError,
  ServerError,
} from '@/lib/api/errors/api-errors';
import { clientApiFetcher } from '@/lib/api/fetcher/client';
import { cn } from '@/lib/utils';
import { User as UserType } from '@/types/auth';

import { GoogleButton } from './google-button';
import { LinkedinButton } from './linkedin-button';
import { TermsPrivacy } from './terms-privacy';

export function SignupForm({
  className,
  referralUsername,
  ...props
}: React.ComponentPropsWithoutRef<'div'> & {
  referralUsername?: string;
}) {
  const { mutate, getGoogleAuthUrl, getLinkedinAuthUrl } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const [modalError, setModalError] = useState('');
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const searchParams = useSearchParams();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSignupFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    setError('');

    // Validations
    if (!acceptTerms) {
      setError(
        'Please accept the Terms of Service and Privacy Policy to continue',
      );
      return;
    }

    if (password !== confirmPassword) {
      setError(
        "The Passwords you entered don't match. Make sure both fields are the same.",
      );
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!/[a-zA-Z]/.test(password)) {
      setError('Password must contain at least 1 alphabetic character');
      return;
    }

    if (!/\d/.test(password)) {
      setError('Password must contain at least 1 digit');
      return;
    }

    if (!/[!@#$%^&*]/.test(password)) {
      setError('Password must contain at least 1 special character');
      return;
    }

    if (!/^[a-zA-Z0-9\s]+$/.test(fullName)) {
      setError('Full name must not contain special characters');
      return;
    }

    setLoading(true);

    clientApiFetcher<{ message: string; user: UserType }>(
      getApiUrl(apiRoutes.auth.signup),
      {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
          fullName,
          ref: referralUsername,
        }),
        skipRefresh: true,
      },
    )
      .then(async ({ user, message }) => {
        if (user.id) {
          await mutate({ user, message }, false);
          router.push(
            `/auth/signup/verify-email${
              searchParams.get('callbackUrl')
                ? `?callbackUrl=${encodeURIComponent(searchParams.get('callbackUrl')!)}`
                : ''
            }`,
          );
        }
      })
      .catch((err) => {
        if (err instanceof RateLimitError) {
          setModalError(
            `Too many signup attempts. 
            Your IP is temporarily locked to keep our service secure. 
            Please try again after 10 minutes.`,
          );
          setErrorModalOpen(true);
        } else if (err instanceof ServerError) {
          setError(err.message || 'An unexpected error occurred');
        } else if (err instanceof NetworkError) {
          setError('Network error. Please check your internet connection.');
        } else if (err instanceof BadRequestError) {
          setError(err.message || 'An unexpected error occurred');
        } else {
          setError(err.message || 'An unexpected error occurred');
        }

        setLoading(false);
      });
  };

  const handleModalOpen = (open: boolean) => {
    if (!open) {
      setError('');
      setModalError('');
    }
    setErrorModalOpen(open);
  };

  return (
    <>
      <div className={cn('space-y-4', className)} {...props}>
        <form onSubmit={handleSignupFormSubmit} className='space-y-4'>
          <div className='space-y-2'>
            <div className='relative'>
              <User className='absolute left-3 top-3 size-4 text-brand-green' />
              <Input
                type='text'
                placeholder='Full name'
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className='border-brand-green pl-10 focus:border-brand-green focus:ring-brand-green'
                required
              />
            </div>
            <div className='relative'>
              <Mail className='absolute left-3 top-3 size-4 text-brand-green' />
              <Input
                type='email'
                placeholder='Email address'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className='border-brand-green pl-10 focus:border-brand-green focus:ring-brand-green'
                required
              />
            </div>
            <div className='relative'>
              <Lock className='absolute left-3 top-3 size-4 text-brand-green' />
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder='Password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className='border-brand-green px-10 focus:border-brand-green focus:ring-brand-green'
                required
              />
              <button
                onClick={togglePasswordVisibility}
                type='button'
                className='absolute right-3 top-3 text-gray-400 hover:text-gray-600'
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <div className='relative'>
              <Lock className='absolute left-3 top-3 size-4 text-brand-green' />
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder='Confirm password'
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className='border-brand-green px-10 focus:border-brand-green focus:ring-brand-green'
                required
              />
              <button
                onClick={toggleConfirmPasswordVisibility}
                type='button'
                className='absolute right-3 top-3 text-gray-400 hover:text-gray-600'
              >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <TermsPrivacy accepted={acceptTerms} onChange={setAcceptTerms} />
          {error && <div className='text-sm text-red-500'>{error}</div>}
          <Button
            type='submit'
            variant='default'
            disabled={loading}
            className='w-full'
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </Button>
          <div className='relative py-2'>
            <div className='absolute inset-0 flex items-center'>
              <span className='w-full border-t border-brand-green' />
            </div>
            <div className='relative flex justify-center text-xs uppercase'>
              <span className='bg-white px-2 text-green-600'>OR</span>
            </div>
          </div>
          <GoogleButton
            text='sign-up'
            callbackUrl={searchParams.get('callbackUrl') || undefined}
            ref={referralUsername}
            onClick={() => {
              if (!acceptTerms) {
                setError(
                  'Please accept the Terms of Use and Privacy Policy to continue',
                );
              } else {
                router.push(
                  getGoogleAuthUrl({
                    callbackUrl: searchParams.get('callbackUrl') || undefined,
                    ref: referralUsername,
                  }),
                );
              }
            }}
          />
          <LinkedinButton
            text='sign-up'
            callbackUrl={searchParams.get('callbackUrl') || undefined}
            ref={referralUsername}
            onClick={() => {
              if (!acceptTerms) {
                setError(
                  'Please accept the Terms of Use and Privacy Policy to continue',
                );
              } else {
                router.push(
                  getLinkedinAuthUrl({
                    callbackUrl: searchParams.get('callbackUrl') || undefined,
                    ref: referralUsername,
                  }),
                );
              }
            }}
          />
        </form>
      </div>
      {/* Error Dialog */}
      <Dialog onOpenChange={handleModalOpen} open={errorModalOpen}>
        <DialogContent className='px-12 py-8 sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle className='flex items-center justify-center p-2'>
              <LockKeyhole className='size-10 text-red-700' />
            </DialogTitle>
            <DialogDescription className='whitespace-pre-line text-center text-lg font-medium text-red-700'>
              {modalError}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
}
